import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {TranslateService} from '@ngx-translate/core';
import {ModalDialogDismissOption} from '@shared/confirmation-dialog/modal-dialog-dismiss-option';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {TableauPageConfigurationService} from '@services/pages/tableau-page/tableau-page-configuration.service';
import {MenuItemType} from '@models/sidebar/menu-item-type';
import {TableauPage} from '@models/sidebar/response/tableau-page';
import {TableauProject} from '@models/machine/tableau-project';
import {Machine} from '@models/machine/machine';
import {OffsetType} from '@models/pages/tableau-page/offset-type';
import {FirebaseAuthenticationService} from '@services/firebase/auth/firebase-authentication.service';
import {Subject, switchMap, takeUntil} from 'rxjs';
import {NotificationService} from '@services/notification/notification.service';

@Component({
    selector: 'scitis-edit-tableau-page',
    templateUrl: './edit-tableau-page.component.html',
    styleUrls: ['./edit-tableau-page.component.scss'],
    standalone: false
})
export class EditTableauPageComponent implements OnInit, OnDestroy {

  @Input()
  public pageParentType: MenuItemType = MenuItemType.MACHINE;
  @Input()
  public machine: Machine = new Machine();
  @Input()
  public tableauPageConfiguration: TableauPage = new TableauPage('id', 'name', !this.isUnderMachineGroup);

  public tableauEditForm: UntypedFormGroup = new UntypedFormGroup({});

  public isLoadingTableauViews: boolean = false;
  public isLoadingTableauProjects: boolean = false;
  public startTimeLabel: string = '';
  public endTimeLabel: string = '';
  public offsetTypesArray: OffsetType[];

  public isFixedRange: boolean = true;
  public isFixedOffset: boolean = false;
  public tableauProjects: TableauProject[] = [];
  public tableauViews: TableauPage[] = [];
  public selectProjectLabel: string = '';
  public selectViewLabel: string = '';
  private organizationId: string = '';
  private editTitle: string = '';

  private readonly COMPONENT_NAME = 'EditTableauPageComponent';
  private $destroy: Subject<boolean> = new Subject<boolean>();

  constructor(private activeModal: NgbActiveModal,
              private tableauPageConfigurationService: TableauPageConfigurationService,
              private translateService: TranslateService,
              private firebaseAuthenticationService: FirebaseAuthenticationService,
              private notificationService: NotificationService) {
    this.setTranslations();
    this.offsetTypesArray = Object.values(OffsetType);
  }

  get title(): string {
    return this.editTitle;
  }

  get formControls() {
    return this.tableauEditForm.controls;
  }

  public get isUnderMachineGroup(): boolean {
    return this.pageParentType === MenuItemType.MACHINE_GROUP;
  }

  public viewsCustomSearchFunction(term: string, item: TableauPage): boolean {
    return item.name.toLowerCase().includes(term.toLowerCase()) ||
        item.tableauId.toLowerCase().includes(term.toLowerCase());
  }


  ngOnInit() {
    this.generateTableauEditForm();
    if (this.isUnderMachineGroup) {
      this.getTableauProjects();
    } else if (this.machine) {
      this.tableauPageConfiguration.tableauProjectId = this.machine.tableauProjectId;
      this.organizationId = String(this.machine.parentId);
      this.getTableauViewsForOrganizationAndProject(this.organizationId, this.tableauPageConfiguration.tableauProjectId);
    }

    this.determineIfFixedRange();
  }

  ngOnDestroy(): void {
    this.$destroy.next(true);
    this.$destroy.complete();
  }


  onClose(): void {
    this.activeModal.dismiss(ModalDialogDismissOption.CLOSE);
  }

  onCancel(_$event: boolean): void {
    this.activeModal.dismiss(ModalDialogDismissOption.CANCEL);
  }

  onSave(_$event: boolean): void {
    if (this.tableauEditForm.invalid) {
      return;
    }

    this.setTableauPageConfiguration();
    this.activeModal.close(this.tableauPageConfiguration);
  }

  disableSaveButton(): boolean {
    if ((this.isFixedRange && this.formControls.tableauView.errors) || ((!this.isFixedRange) && (this.formControls.startTimeOffsetValue.errors || this.formControls.tableauView.errors))) {
      return true;
    }
    return false;
  }

  selectDateRange(isFixed: boolean) {
    if (isFixed) {
      this.tableauPageConfiguration.startTimeOffsetValue = 0;
      this.formControls.startTimeOffsetValue.setValue(0);
      this.tableauPageConfiguration.startTimeOffsetType = OffsetType.HOUR;
      this.formControls.startTimeOffsetType.setValue(OffsetType.HOUR);
      this.tableauPageConfiguration.useEndTimeParameterValue = true;
      this.formControls.useEndTimeParameterValue.setValue(true);
      this.tableauPageConfiguration.useStartTimeParameterValue = true;
      this.formControls.startTimeOffsetValue.disable();
    } else {
      this.tableauPageConfiguration.useStartTimeParameterValue = false;
      this.formControls.startTimeOffsetValue.enable();
    }

    this.isFixedRange = isFixed;
  }

  getStartDate() {
    return this.getDateComponent(this.tableauPageConfiguration.startTimeParameterValue);
  }

  getStartTime() {
    return this.getTimeComponent(this.tableauPageConfiguration.startTimeParameterValue);
  }

  getEndDate() {
    return this.getDateComponent(this.tableauPageConfiguration.endTimeParameterValue);
  }

  getEndTime() {
    return this.getTimeComponent(this.tableauPageConfiguration.endTimeParameterValue);
  }

  onFocusOutStartTime(event) {
    if (!event || !event.date || !event.time || event.time.hour === undefined || event.time.minute === undefined) {
      return;
    }
    this.tableauPageConfiguration.startTimeParameterValue = this.tableauPageConfigurationService.getDate(event);
  }

  onFocusOutEndTime(event) {
    if (!event || !event.date || !event.time || event.time.hour === undefined || event.time.minute === undefined) {
      return;
    }
    this.tableauPageConfiguration.endTimeParameterValue = this.tableauPageConfigurationService.getDate(event);
  }

  onTableauProjectChange($event: TableauProject) {
    this.tableauPageConfiguration.tableauProjectId = $event.id;
    if (this.tableauPageConfiguration.tableauProjectId === '-1') {
      return;
    }
    this.getTableauViewsForOrganizationAndProject(this.organizationId, this.tableauPageConfiguration.tableauProjectId);
  }

  onUseStartTimeOnlyDateChange($event: any) {
    if ($event && $event.target) {
      this.tableauPageConfiguration.useStartTimeOnlyDate = $event.target.checked;
    }
  }

  onUseEndTimeOnlyDateChange($event: any) {
    if ($event && $event.target) {
      this.tableauPageConfiguration.useEndTimeOnlyDate = $event.target.checked;
    }
  }

  onOffsetTypeChange($event: string) {
    this.tableauPageConfiguration.startTimeOffsetType = $event;
  }

  onUseEndTimeParameterValueChange($event: any) {
    if ($event && $event.target) {
      this.tableauPageConfiguration.useEndTimeParameterValue = $event.target.checked;
      this.isFixedOffset = !this.isFixedOffset;
    }
  }

  private determineIfFixedRange() {
    if (!this.tableauPageConfiguration.startTimeOffsetType || !this.tableauPageConfiguration.startTimeOffsetValue || this.tableauPageConfiguration.startTimeOffsetValue <= 0) {
      this.isFixedRange = true;
      this.formControls.startTimeOffsetValue.disable();
    } else {
      this.isFixedRange = false;
      this.formControls.startTimeOffsetValue.enable();
    }
  }

  private getDateComponent(value: number) {
    let date;
    if (!value) {
      date = new Date();
    } else {
      date = new Date(value);
    }

    date.setHours(0);
    date.setMinutes(0);
    date.setSeconds(0);

    return String(date.getTime());
  }

  private getTimeComponent(value: number) {
    let hour, minute, second;
    if (!value) {
      hour = 0;
      minute = 0;
      second = 0;
    } else {
      const date = new Date(value);
      hour = date.getHours();
      minute = date.getMinutes();
      second = date.getSeconds();
    }
    return {hour: hour, minute: minute, second: second};
  }

  private getTableauProjects() {
    this.isLoadingTableauProjects = true;
    this.formControls.tableauProject.disable();
    if (!this.isUnderMachineGroup) {
      return;
    }

    this.firebaseAuthenticationService.getLoggedUserDetails().pipe(
        takeUntil(this.$destroy),
        switchMap((user) => {
          this.organizationId = String(user.parentId);
          return this.tableauPageConfigurationService.getTableauProjectsForOrganisation(0, Number(user.parentId));
        })
    ).subscribe({
      next: (tableauProjects) => {
        this.tableauProjects = [...tableauProjects];
        if (this.tableauPageConfiguration.tableauProjectId) {
          this.getTableauViewsForOrganizationAndProject(this.organizationId, this.tableauPageConfiguration.tableauProjectId);
        }
        this.isLoadingTableauProjects = false;
        this.formControls.tableauProject.enable();
      }, error: (error) => {
        console.log(error);
        this.isLoadingTableauProjects = false;
        this.formControls.tableauProject.enable();
      }
    });

  }

  private getTableauViewsForOrganizationAndProject(organizationId: string, tableauProjectId: string): void {
    this.isLoadingTableauViews = true;
    this.tableauPageConfigurationService.getTableauViewsForOrganizationAndProject(organizationId, tableauProjectId)
    .pipe(takeUntil(this.$destroy))
    .subscribe({
      next: (tableauViewsForThisProject) => {
        this.tableauViews = tableauViewsForThisProject;
        this.isLoadingTableauViews = false;
      }, error: () => {
        this.isLoadingTableauViews = false;
        this.notificationService.warning(
            this.translateService.instant('There is a problem with the tableau configuration.'),
            this.COMPONENT_NAME
        );
      }
    });

  }

  private setTableauPageConfiguration(): void {
    this.tableauPageConfiguration.tableauId = this.formControls.tableauView.value;
    this.tableauPageConfiguration.tableauProjectId = this.formControls.tableauProject.value;
    this.tableauPageConfiguration.startTimeParameterName = this.formControls.startTimeParameterName.value;
    this.tableauPageConfiguration.endTimeParameterName = this.formControls.endTimeParameterName.value;
    this.tableauPageConfiguration.additionalParameters = this.formControls.additionalParameters.value;
    this.tableauPageConfiguration.startTimeOffsetValue = this.formControls.startTimeOffsetValue.value;
    this.tableauPageConfiguration.startTimeOffsetType = this.formControls.startTimeOffsetType.value;
    this.tableauPageConfiguration.useEndTimeParameterValue = this.formControls.useEndTimeParameterValue.value;
  }

  private generateTableauEditForm() {
    this.tableauEditForm = new UntypedFormGroup({
      tableauProject: new UntypedFormControl(this.tableauPageConfiguration.tableauProjectId),
      tableauView: new UntypedFormControl(this.tableauPageConfiguration.tableauId, [Validators.required]),
      startTimeParameterName: new UntypedFormControl(this.tableauPageConfiguration.startTimeParameterName),
      endTimeParameterName: new UntypedFormControl(this.tableauPageConfiguration.endTimeParameterName),
      additionalParameters: new UntypedFormControl(this.tableauPageConfiguration.additionalParameters),
      startTimeOffsetValue: new UntypedFormControl(this.tableauPageConfiguration.startTimeOffsetValue, [Validators.required, Validators.min(1)]),
      startTimeOffsetType: new UntypedFormControl(this.tableauPageConfiguration.startTimeOffsetType),
      useEndTimeParameterValue: new UntypedFormControl(this.tableauPageConfiguration.useEndTimeParameterValue),
    });
  }

  private setTranslations(): void {
    this.editTitle = this.translateService.instant('Edit Tableau Page');
    this.startTimeLabel = this.translateService.instant('Start');
    this.endTimeLabel = this.translateService.instant('End');
    this.selectProjectLabel = this.translateService.instant('Select tableau project');
    this.selectViewLabel = this.translateService.instant('Select tableau view');
  }
}
